export function formatCurrency(amount) {
    if (amount === null || amount === undefined) return "$0.00";
    return Number(amount).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
    });
}

export function formatCurrencyCompact(amount) {
    if (amount === null || amount === undefined) return "$0";
    return Number(amount).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}

export function formatCurrencyShort(amount) {
    if (amount === null || amount === undefined) return "$0";
    if (Math.abs(amount) >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (Math.abs(amount) >= 1000) {
        return `$${Math.round(amount / 1000)}K`;
    }
    return formatCurrencyCompact(amount);
}

export function formatCurrencyAbs(amount) {
    return Math.abs(amount).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
    });
}

/**
 * Percentage formatting.
 * @param {number} value - The percentage value
 * @param {boolean} signed - Whether to include a +/- sign (default false)
 * @param {number} decimals - Number of decimal places (default 2)
 */
export function formatPercent(value, { signed = false, decimals = 2 } = {}) {
    if (value === null || value === undefined) return "0.00%";
    const formatted = `${Number(value).toFixed(decimals)}%`;
    if (signed) {
        const sign = value >= 0 ? "+" : "";
        return `${sign}${formatted}`;
    }
    return formatted;
}

/**
 * Date formatting: "Mar 10, 2026"
 */
export function formatDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

/**
 * Long date formatting: "March 10, 2026"
 */
export function formatDateLong(dateStr) {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

/**
 * Short date formatting (no year): "Mar 10"
 */
export function formatDateShort(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

/**
 * Relative time: "just now", "5m ago", "3h ago", or "Mar 10"
 */
export function formatSyncTime(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Account subtype display name: "401k" -> "401(k)", "money_market" -> "Money Market"
 */
export function formatAccountSubtype(subtype) {
    if (!subtype) return "";

    const specialCases = {
        "checking": "Checking",
        "savings": "Savings",
        "401k": "401(k)",
        "_401k": "401(k)",
        "ira": "IRA",
        "roth": "Roth IRA",
        "roth_401k": "Roth 401(k)",
        "cd": "CD",
        "hsa": "HSA",
        "brokerage": "Brokerage",
        "money_market": "Money Market",
        "credit_card": "Credit Card",
        "student": "Student Loan",
        "mortgage": "Mortgage",
        "auto": "Auto Loan",
        "personal": "Personal Loan",
        "paypal": "PayPal",
        "prepaid": "Prepaid",
        "ebt": "EBT",
    };

    const lower = subtype.toLowerCase().replace(/^_/, "");
    if (specialCases[lower]) {
        return specialCases[lower];
    }

    return lower
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Gain/loss percentage: "+5.2%" or "-3.1%", or null if cost basis is zero.
 */
export function formatGainLossPercent(gainLoss, costBasis) {
    if (!costBasis || costBasis <= 0) return null;
    const percent = (gainLoss / costBasis) * 100;
    return `${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%`;
}
